/**
 * Settings Store - Mellow user preferences
 * Simple direct Supabase calls (like chats$) - no complex local-first sync
 */

import { observable } from '@legendapp/state';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';

// Settings state
export const settings$ = observable({
  memorySearchEnabled: true,  // Default: enabled
  streamingEnabled: false,    // Default: disabled (for safety, beta feature)
  uiLanguage: null,           // 'en' | 'zh' | 'ru' | null
  flashcardFrontDisplay: 'original', // 'original' | 'translation' | 'both'
  isLoading: false,
});

/**
 * Fetch user settings from database
 */
export const fetchSettings = async () => {
  const user = auth$.user.get();
  if (!user) {
    settings$.isLoading.set(false);
    return;
  }

  settings$.isLoading.set(true);

  try {
    // Fetch all settings at once
    const { data, error } = await supabase
      .from('mellow_user_settings')
      .select('setting_key, setting_value')
      .eq('user_id', user.id)
      .eq('deleted', false)
      .in('setting_key', ['memory_search_enabled', 'streaming_enabled', 'ui_language', 'flashcard_front_display']);

    if (error && error.code !== 'PGRST116') {
      // PGRST116 = no rows returned (setting doesn't exist yet)
      console.error('Failed to fetch settings:', error);
    }

    // Process settings
    const settingsMap = {};
    data?.forEach(row => {
      settingsMap[row.setting_key] = row.setting_value;
    });

    // Apply memory_search_enabled
    const enabled = settingsMap['memory_search_enabled'] !== 'false';
    settings$.memorySearchEnabled.set(enabled);

    // Apply streaming_enabled (default: false for safety)
    const streamingEnabled = settingsMap['streaming_enabled'] === 'true';
    settings$.streamingEnabled.set(streamingEnabled);

    // Apply ui_language
    const uiLang = settingsMap['ui_language'] || null;
    settings$.uiLanguage.set(uiLang);

    // Apply flashcard_front_display
    const frontDisplay = settingsMap['flashcard_front_display'] || 'original';
    settings$.flashcardFrontDisplay.set(frontDisplay);
  } catch (err) {
    console.error('Failed to fetch settings:', err);
  } finally {
    settings$.isLoading.set(false);
  }
};

/**
 * Update memory search enabled setting
 * @param {boolean} enabled - Whether memory search should be enabled
 */
export const updateMemorySearchEnabled = async (enabled) => {
  const user = auth$.user.get();
  if (!user) return;

  // Optimistic update
  settings$.memorySearchEnabled.set(enabled);

  try {
    // Upsert setting to database
    const { error } = await supabase
      .from('mellow_user_settings')
      .upsert({
        user_id: user.id,
        setting_key: 'memory_search_enabled',
        setting_value: enabled ? 'true' : 'false',
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('Failed to update memory search setting:', error);
      // Revert optimistic update on error
      settings$.memorySearchEnabled.set(!enabled);
    }
  } catch (err) {
    console.error('Failed to update memory search setting:', err);
    // Revert optimistic update on error
    settings$.memorySearchEnabled.set(!enabled);
  }
};

/**
 * Get current memory search enabled value
 * @returns {boolean}
 */
export const getMemorySearchEnabled = () => {
  return settings$.memorySearchEnabled.get();
};

/**
 * Update streaming enabled setting
 * @param {boolean} enabled - Whether streaming should be enabled
 */
export const updateStreamingEnabled = async (enabled) => {
  const user = auth$.user.get();
  if (!user) return;

  // Optimistic update
  settings$.streamingEnabled.set(enabled);

  try {
    // Upsert setting to database
    const { error } = await supabase
      .from('mellow_user_settings')
      .upsert({
        user_id: user.id,
        setting_key: 'streaming_enabled',
        setting_value: enabled ? 'true' : 'false',
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('Failed to update streaming setting:', error);
      // Revert optimistic update on error
      settings$.streamingEnabled.set(!enabled);
    }
  } catch (err) {
    console.error('Failed to update streaming setting:', err);
    // Revert optimistic update on error
    settings$.streamingEnabled.set(!enabled);
  }
};

/**
 * Get current streaming enabled value
 * @returns {boolean}
 */
export const getStreamingEnabled = () => {
  return settings$.streamingEnabled.get();
};

/**
 * Update UI language setting
 * @param {string} langCode - Language code ('en', 'zh', 'ru')
 */
export const updateUILanguage = async (langCode) => {
  const user = auth$.user.get();
  if (!user) return;

  // Validate language code
  if (!['en', 'zh', 'ru'].includes(langCode)) {
    console.error('Invalid language code:', langCode);
    return;
  }

  // Optimistic update
  settings$.uiLanguage.set(langCode);

  try {
    // Upsert setting to database
    const { error } = await supabase
      .from('mellow_user_settings')
      .upsert({
        user_id: user.id,
        setting_key: 'ui_language',
        setting_value: langCode,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('Failed to update UI language:', error);
      // Note: Don't revert - i18n already changed, keep them in sync
    }
  } catch (err) {
    console.error('Failed to update UI language:', err);
  }
};

/**
 * Update flashcard front display setting
 * @param {string} mode - 'original' | 'translation' | 'both'
 */
export const updateFlashcardFrontDisplay = async (mode) => {
  const user = auth$.user.get();
  if (!user) return;

  // Optimistic update
  settings$.flashcardFrontDisplay.set(mode);

  try {
    // Upsert setting to database
    const { error } = await supabase
      .from('mellow_user_settings')
      .upsert({
        user_id: user.id,
        setting_key: 'flashcard_front_display',
        setting_value: mode,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('Failed to update flashcard front display:', error);
      // Revert optimistic update on error
      settings$.flashcardFrontDisplay.set('original');
    }
  } catch (err) {
    console.error('Failed to update flashcard front display:', err);
    // Revert optimistic update on error
    settings$.flashcardFrontDisplay.set('original');
  }
};

/**
 * Save onboarding intents to database
 * @param {string[]} intents - Array of intent IDs (e.g., ['phrases', 'slang'])
 */
export const saveOnboardingIntents = async (intents) => {
  const user = auth$.user.get();
  if (!user) return;

  try {
    // Save intents as JSON string in mellow_user_settings
    const { error } = await supabase
      .from('mellow_user_settings')
      .upsert({
        user_id: user.id,
        setting_key: 'onboarding_intents',
        setting_value: JSON.stringify(intents),
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,setting_key'
      });

    if (error) {
      console.error('Failed to save onboarding intents:', error);
      throw error;
    }
  } catch (err) {
    console.error('Failed to save onboarding intents:', err);
    throw err;
  }
};

